import {gql} from '@apollo/client';
import {createStyles} from '@mantine/core';
import {captureException} from '@sentry/browser';
import React, {ErrorInfo, ReactNode} from 'react';

import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {ChatMessageThread} from 'llm/chat-library/ChatMessageThread';
import {ChatExportRenderProvider} from 'llm/chat-library/ChatRenderContext';
import {ChatExportAppendix} from 'llm/chat-library/export/ChatExportAppendix';
import {getChatNameRenderInfo} from 'llm/chat-library/sidepanel/ChatNameUtil';
import {useMessageThread} from 'llm/chat-library/useMessageThread';
import PrintableWrapper from 'notebook/mediocre/print/PrintableWrapper';
import {errorIndicatorAttrs, loadingIndicatorAttrs} from 'notebook/util/ExportInstrumentation';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {ChatPrintViewQuery, ChatPrintViewQueryVariables} from './__generated/ChatPrintViewQuery';

export const CHAT_PRINT_VIEW_QUERY = gql`
  query ChatPrintViewQuery($chatSessionId: StrictString!) @skipAudit @engComponent(name: LLM) {
    chatSession(id: $chatSessionId) {
      ... on ChatSessionNode {
        id
        name
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const ERROR_BANNER_PADDING = {paddingTop: 20};

const useStyles = createStyles(() => ({
  printContainer: {
    maxWidth: 900,
    margin: '0 auto',
    padding: '24px 16px 48px',
  },
}));

/**
 * Printable chat transcript for browser print and headless PDF export.
 *
 * - PrintableWrapper blocks on nested resources and sizes wide content for print.
 * - ErrorBoundary ensures render failures surface as error indicators for export jobs.
 * - InnerChatPrintView loads the session and renders ChatMessageThread in viewer mode with a tool-call appendix.
 */
export default function ChatPrintView(props: {chatSessionId: string}): JSX.Element {
  const titleQuery = useBenchlingQuery<ChatPrintViewQuery, ChatPrintViewQueryVariables>(
    CHAT_PRINT_VIEW_QUERY,
    {variables: {chatSessionId: props.chatSessionId}}
  );
  const title =
    titleQuery.type === QueryResultType.SUCCESS && !isInaccessible(titleQuery.data.chatSession)
      ? getChatNameRenderInfo({
          id: props.chatSessionId,
          name: titleQuery.data.chatSession.name,
        }).text
      : 'Chat';

  return (
    <PrintableWrapper title={title}>
      <ErrorBoundary>
        <InnerChatPrintView chatSessionId={props.chatSessionId} />
      </ErrorBoundary>
    </PrintableWrapper>
  );
}

function InnerChatPrintView({chatSessionId}: {chatSessionId: string}): JSX.Element {
  const {classes} = useStyles();
  const {
    events,
    progressData,
    citationData,
    isLoading,
    errorMessage,
    chatSessionDisplayName,
    hasChatScopedWriteApproval,
    messageSenderContext,
    sessionCreatorFallback,
  } = useMessageThread({
    chatSessionId,
    includeToolCalls: true,
    disableRealtimeUpdates: true,
    viewerMode: true,
  });

  // Match the progress timeline: citationData includes citation-only tools (e.g. CompleteAnswer)
  // that are intentionally hidden from progress via shouldDisplayTool.
  const toolCalls = progressData?.toolCalls ?? [];

  if (errorMessage != null && events.length === 0) {
    return <ErrorBannerWithIndicator reason="network">{errorMessage}</ErrorBannerWithIndicator>;
  }

  if (isLoading && events.length === 0) {
    return (
      <div {...loadingIndicatorAttrs()}>
        <CenteredHorizontalThrobber />
      </div>
    );
  }

  return (
    <ChatExportRenderProvider>
      <div className={classes.printContainer}>
        <ChatMessageThread
          events={events}
          chatSessionId={chatSessionId}
          chatSessionDisplayName={chatSessionDisplayName}
          banner={null}
          actionButton={null}
          readonly={true}
          approvalActionsReadonly={true}
          hasChatScopedWriteApproval={hasChatScopedWriteApproval}
          progressData={progressData}
          citationData={citationData}
          messageSenderContext={messageSenderContext}
          sessionCreatorFallback={sessionCreatorFallback}
          viewerMode={true}
        />
        <ChatExportAppendix toolCalls={toolCalls} chatSessionId={chatSessionId} />
      </div>
    </ChatExportRenderProvider>
  );
}

function ErrorBannerWithIndicator(props: {reason: string; children: ReactNode}): JSX.Element {
  return (
    <div {...errorIndicatorAttrs()} data-error-reason={props.reason} style={ERROR_BANNER_PADDING}>
      <InlineBanner bannerType={InlineBannerType.ERROR} text={props.children} />
    </div>
  );
}

interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<{children: React.ReactNode}> {
  public override state: ErrorBoundaryState = {hasError: false};

  static getDerivedStateFromError(_error: Error): ErrorBoundaryState {
    return {hasError: true};
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    captureException(error, {extra: {errorInfo}});
  }

  override render(): ReactNode {
    return this.state.hasError ? (
      <ErrorBannerWithIndicator reason="js">
        An error occurred while rendering the chat.
      </ErrorBannerWithIndicator>
    ) : (
      this.props.children
    );
  }
}
